import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaTiktok, FaTwitter, FaYoutube, FaStar, FaCheckCircle } from 'react-icons/fa';

const Packages = () => {
  const platforms = [
    {
      name: 'Facebook',
      icon: <FaFacebook className="text-4xl" />,
      color: 'bg-blue-600',
      description: 'Boost your Facebook page followers and engagement',
      path: '/packages/facebook'
    },
    {
      name: 'Instagram',
      icon: <FaInstagram className="text-4xl" />,
      color: 'bg-pink-600',
      description: 'Increase your Instagram followers and likes',
      path: '/packages/instagram'
    },
    {
      name: 'TikTok',
      icon: <FaTiktok className="text-4xl" />,
      color: 'bg-black',
      description: 'Grow your TikTok followers and video views',
      path: '/packages/tiktok'
    },
    {
      name: 'Twitter',
      icon: <FaTwitter className="text-4xl" />,
      color: 'bg-sky-500',
      description: 'Enhance your Twitter followers and engagement',
      path: '/packages/twitter'
    },
    {
      name: 'YouTube',
      icon: <FaYoutube className="text-4xl" />,
      color: 'bg-red-600',
      description: 'Increase your YouTube subscribers and views',
      path: '/packages/youtube'
    }
  ];

  const commonFeatures = [
    'Real and Active Followers',
    'Fast Delivery',
    '24/7 Customer Support',
    '30-Day Money Back Guarantee',
    'Safe & Secure Methods',
    'No Password Required'
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="hero-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Choose Your Platform
            </h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Select your preferred social media platform and choose from our Standard, Medium, or Pro packages
            </p>
          </div>
        </div>
      </section>

      {/* Platform Selection */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-600 mb-4">
              Select Your Platform
            </h2>
            <p className="text-gray-600 text-lg">
              We offer comprehensive packages for all major social media platforms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <Link
                key={platform.name}
                to={platform.path}
                className="card p-8 group hover:scale-105 transition-all duration-300"
              >
                <div className={`${platform.color} w-20 h-20 rounded-full flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {platform.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 text-center">
                  {platform.name}
                </h3>
                <p className="text-white text-center mb-6">
                  {platform.description}
                </p>
                <div className="flex items-center justify-center text-white font-semibold group-hover:text-purple-200">
                  View Packages
                  <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Package Tiers Overview */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-600 mb-4">
              Package Tiers
            </h2>
            <p className="text-gray-600 text-lg">
              Three flexible tiers to meet your growth needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="package-card">
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">Standard</h3>
                <div className="text-4xl font-bold text-white mb-2">500</div>
                <div className="text-lg text-white mb-6">Followers</div>
                <div className="text-3xl font-bold text-white mb-8">$9.99</div>
                <ul className="space-y-3 mb-8 text-left">
                  {commonFeatures.slice(0, 4).map((feature, index) => (
                    <li key={index} className="flex items-center text-white">
                      <FaCheckCircle className="text-green-400 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="package-card featured">
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">Medium</h3>
                <div className="text-5xl font-bold text-white mb-2">1,000</div>
                <div className="text-lg text-white mb-6">Followers</div>
                <div className="text-3xl font-bold text-white mb-8">$19.99</div>
                <ul className="space-y-3 mb-8 text-left">
                  {commonFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center text-white">
                      <FaCheckCircle className="text-green-400 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="package-card">
              <div className="p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">Pro</h3>
                <div className="text-4xl font-bold text-white mb-2">2,000</div>
                <div className="text-lg text-white mb-6">Followers</div>
                <div className="text-3xl font-bold text-white mb-8">$39.99</div>
                <ul className="space-y-3 mb-8 text-left">
                  {commonFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center text-white">
                      <FaCheckCircle className="text-green-400 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-800 mb-4">
              Why Choose Our Packages?
            </h2>
            <p className="text-purple-600 text-lg">
              We provide best value for your social media growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Real Growth',
                description: '100% real and active followers who engage with your content',
                icon: <FaStar className="text-2xl" />
              },
              {
                title: 'Fast Results',
                description: 'See noticeable results within 24-48 hours',
                icon: <FaStar className="text-2xl" />
              },
              {
                title: 'Safe & Secure',
                description: 'No passwords required, completely safe for your accounts',
                icon: <FaCheckCircle className="text-2xl" />
              },
              {
                title: '24/7 Support',
                description: 'Our support team is always here to help you',
                icon: <FaStar className="text-2xl" />
              },
              {
                title: 'Money Back Guarantee',
                description: '30-day guarantee if you\'re not satisfied',
                icon: <FaCheckCircle className="text-2xl" />
              },
              {
                title: 'Best Prices',
                description: 'Competitive pricing with no hidden fees',
                icon: <FaStar className="text-2xl" />
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 mx-auto mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-purple-800 mb-2">{benefit.title}</h3>
                <p className="text-purple-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 hero-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Start Your Growth Journey Today
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Choose your platform and select the perfect package for your needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="btn-primary bg-white text-white hover:bg-purple-50">
              Get Started Now
            </Link>
            <Link to="/contact" className="btn-secondary border-white text-white hover:bg-white hover:text-purple-600">
              Contact Support
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
